import { useEffect, useRef } from 'react'
import { track } from './analytics'

/**
 * Один раз за сессию страницы фиксируем глубину прокрутки 25/50/75/100%.
 * Срабатывает по высоте document.documentElement, т.к. body может иметь
 * `min-height: 100vh` и не отражать реальную высоту.
 *
 * Используем `passive: true` + `requestAnimationFrame`, чтобы не блокировать
 * скролл на мобильных. Каждый порог стреляет максимум один раз.
 */
const THRESHOLDS = [25, 50, 75, 100] as const

type Threshold = (typeof THRESHOLDS)[number]
type ThresholdEvent = `scroll_${Threshold}`

export function useScrollDepth() {
  const fired = useRef<Set<Threshold>>(new Set())
  const rafId = useRef<number | null>(null)

  useEffect(() => {
    const measure = () => {
      rafId.current = null
      const doc = document.documentElement
      const scrollTop = window.scrollY || doc.scrollTop
      const max = doc.scrollHeight - window.innerHeight
      if (max <= 0) return
      const pct = Math.min(100, Math.round((scrollTop / max) * 100))

      for (const t of THRESHOLDS) {
        if (pct >= t && !fired.current.has(t)) {
          fired.current.add(t)
          track(`scroll_${t}` as ThresholdEvent, { pct })
        }
      }
    }

    const onScroll = () => {
      if (rafId.current !== null) return
      rafId.current = requestAnimationFrame(measure)
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    // Сразу измерим — если страница короткая и сразу видна целиком,
    // 100% должно засчитаться без ожидания скролла.
    measure()

    return () => {
      window.removeEventListener('scroll', onScroll)
      if (rafId.current !== null) cancelAnimationFrame(rafId.current)
    }
  }, [])
}
